import { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Box, Button, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid } from '@mui/material';
import { getTrips, createTrip, updateTrip, deleteTrip, exportTrips, type TripPayload } from '../api/tripApi';
import { listCompanies, type Company } from '../api/companyApi';
import { getAllChauffeurs } from '../api/userApi';
import { getAllCamions } from '../api/camionApi';
import type { Camion, Chauffeur, Trip } from '../types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

export default function Trips() {
  const [rows, setRows] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [drivers, setDrivers] = useState<Chauffeur[]>([]);
  const [trucks, setTrucks] = useState<Camion[]>([]);
  const [filters, setFilters] = useState<{ start?: string; end?: string; driverId?: string; companyId?: string }>({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Trip | null>(null);
  const [form, setForm] = useState<Partial<TripPayload>>({ date: new Date().toISOString().slice(0,10), bl_raw: '', bl_numbers: [], weight_t: undefined, amount: undefined, comment: '' } as any);

  const load = async () => {
    const res = await getTrips(filters);
    const data = (res as any).rows || (res as any).trips || [];
    setRows(data);
    setCount((res as any).count || data.length);
  };

  useEffect(() => { (async () => {
    const [cs, ds, ts] = await Promise.all([listCompanies(), getAllChauffeurs(), getAllCamions() as any]);
    setCompanies(cs);
    setDrivers(ds);
    setTrucks(Array.isArray(ts.camions) ? ts.camions : ts);
    await load();
  })(); }, []);

  const applyFilters = async () => { await load(); };

  const onSubmit = async () => {
    if (!form.date || !form.driverId || !form.truckId || !form.companyId) return;
    const payload: TripPayload = {
      date: new Date(form.date).toISOString(),
      driverId: form.driverId!,
      truckId: form.truckId!,
      companyId: form.companyId!,
      bl_raw: form.bl_raw || undefined,
      bl_numbers: form.bl_numbers || undefined,
      weight_t: form.weight_t ?? undefined,
      amount: form.amount ?? undefined,
      comment: form.comment || undefined,
    };
    if (editing) {
      const updated = await updateTrip(editing.id, payload);
      setRows((prev) => prev.map((r) => r.id === updated.id ? updated : r));
    } else {
      const created = await createTrip(payload);
      setRows((prev) => [created, ...prev]);
    }
    setOpen(false); setEditing(null); setForm({} as any);
  };

  const onEdit = (r: Trip) => { setEditing(r); setForm({ date: r.date.slice(0,10), driverId: r.driverId, truckId: r.truckId, companyId: r.companyId, bl_raw: r.bl_raw || '', bl_numbers: r.bl_numbers || [], weight_t: r.weight_t || undefined, amount: r.amount || undefined, comment: r.comment || '' }); setOpen(true); };
  const onDelete = async (r: Trip) => { if (confirm('Supprimer cette course ?')) { await deleteTrip(r.id); setRows((prev) => prev.filter((x) => x.id !== r.id)); } };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4">Courses</Typography>
          <Typography color="text.secondary">Gérez les courses (date, chauffeur, camion, compagnie, BL, poids, montant).</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => exportTrips({ ...filters }, 'csv')}>Export CSV</Button>
          <Button variant="outlined" onClick={() => exportTrips({ ...filters }, 'xlsx')}>Export Excel</Button>
          <Button variant="outlined" onClick={() => exportTrips({ ...filters }, 'pdf')}>Export PDF</Button>
          <Button variant="contained" onClick={() => { setEditing(null); setForm({} as any); setOpen(true); }}>+ Ajouter</Button>
        </Box>
      </Box>

      <Box mb={2} display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(5, 1fr)' }} gap={2}>
        <TextField type="date" label="Début" InputLabelProps={{ shrink: true }} value={filters.start || ''} onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} />
        <TextField type="date" label="Fin" InputLabelProps={{ shrink: true }} value={filters.end || ''} onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} />
        <TextField select label="Chauffeur" value={filters.driverId || ''} onChange={(e) => setFilters((f) => ({ ...f, driverId: e.target.value || undefined }))}>
          <MenuItem value="">Tous</MenuItem>
          {drivers.map((d) => (<MenuItem key={d.id} value={d.id}>{d.nom}</MenuItem>))}
        </TextField>
        <TextField select label="Compagnie" value={filters.companyId || ''} onChange={(e) => setFilters((f) => ({ ...f, companyId: e.target.value || undefined }))}>
          <MenuItem value="">Toutes</MenuItem>
          {companies.map((c) => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
        </TextField>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => setFilters({})}>Réinitialiser</Button>
          <Button variant="contained" onClick={applyFilters}>Filtrer</Button>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Chauffeur</TableCell>
            <TableCell>Camion</TableCell>
            <TableCell>Compagnie</TableCell>
            <TableCell>BL</TableCell>
            <TableCell align="right">Poids (T)</TableCell>
            <TableCell align="right">Montant</TableCell>
            <TableCell>Commentaire</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{new Date(r.date).toLocaleDateString()}</TableCell>
              <TableCell>{r.driver?.nom || r.driverId}</TableCell>
              <TableCell>{r.truck?.nom || r.truckId}</TableCell>
              <TableCell>{r.company?.name || r.companyId}</TableCell>
              <TableCell>{r.bl_raw}</TableCell>
              <TableCell align="right">{r.weight_t ?? ''}</TableCell>
              <TableCell align="right">{r.amount ?? ''}</TableCell>
              <TableCell>{r.comment || ''}</TableCell>
              <TableCell align="right">
                <IconButton onClick={() => onEdit(r)}><EditIcon /></IconButton>
                <IconButton color="error" onClick={() => onDelete(r)}><DeleteIcon /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Modifier une course' : 'Nouvelle course'}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField type="date" label="Date" fullWidth InputLabelProps={{ shrink: true }} value={(form.date as any) || ''} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select label="Chauffeur" fullWidth value={form.driverId || ''} onChange={(e) => setForm((f) => ({ ...f, driverId: e.target.value }))}>
                  {drivers.map((d) => (<MenuItem key={d.id} value={d.id}>{d.nom}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select label="Camion" fullWidth value={form.truckId || ''} onChange={(e) => setForm((f) => ({ ...f, truckId: e.target.value }))}>
                  {trucks.map((t) => (<MenuItem key={t.id} value={t.id}>{t.nom} {t.immatriculation ? `(${t.immatriculation})` : ''}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Compagnie" fullWidth value={form.companyId || ''} onChange={(e) => setForm((f) => ({ ...f, companyId: e.target.value }))}>
                  {companies.map((c) => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="BL" fullWidth value={form.bl_raw || ''} onChange={(e) => setForm((f) => ({ ...f, bl_raw: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField type="number" label="Poids (T)" fullWidth value={form.weight_t ?? ''} onChange={(e) => setForm((f) => ({ ...f, weight_t: e.target.value === '' ? undefined : Number(e.target.value) }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField type="number" label="Montant" fullWidth value={form.amount ?? ''} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value === '' ? undefined : Number(e.target.value) }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Commentaire" fullWidth value={form.comment || ''} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={onSubmit} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
